import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { externalApiKeysApi } from "../../../api/externalApiKeys";
import styles from "../CRMSettingsPage.module.css";

const DOCUMENTATION_FILES = {
  ru: "/documentation/external_api_user_instruction/external_api_user_instruction_ru.docx",
  en: "/documentation/external_api_user_instruction/external_api_user_instruction_en.docx",
};

const DOCUMENTATION_CONTENT = {
  ru: {
    title: "external_api_docs_title",
    subtitle: "external_api_docs_subtitle",
    sections: [
      {
        title: "external_api_docs_section_1_title",
        paragraphs: [
          "external_api_docs_section_1_body",
        ],
      },
      {
        title: "external_api_docs_section_2_title",
        paragraphs: [
          "external_api_docs_section_2_body",
        ],
      },
      {
        title: "external_api_docs_section_3_title",
        list: [
          "external_api_docs_section_3_item_1",
          "external_api_docs_section_3_item_2",
          "external_api_docs_section_3_item_3",
          "external_api_docs_section_3_item_4",
          "external_api_docs_section_3_item_5",
          "external_api_docs_section_3_item_6",
        ],
      },
      {
        title: "external_api_docs_section_4_title",
        paragraphs: [
          "external_api_docs_section_4_body",
        ],
        list: [
          "external_api_docs_section_4_item_1",
          "external_api_docs_section_4_item_2",
          "external_api_docs_section_4_item_3",
          "external_api_docs_section_4_item_4",
        ],
      },
      {
        title: "external_api_docs_section_5_title",
        paragraphs: [
          "external_api_docs_section_5_body",
        ],
        code: "Authorization: Bearer YOUR_API_KEY",
      },
      {
        title: "external_api_docs_section_6_title",
        code: `fetch("/api/v1/external/contacts", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    "Authorization": "Bearer YOUR_API_KEY"
  },
  body: JSON.stringify({
    full_name: "John Smith",
    email: "john@example.com",
    phone: "+10000000000"
  })
});`,
      },
      {
        title: "external_api_docs_section_7_title",
        paragraphs: [
          "external_api_docs_section_7_body",
        ],
      },
      {
        title: "external_api_docs_section_8_title",
        paragraphs: [
          "external_api_docs_section_8_body",
        ],
      },
      {
        title: "external_api_docs_section_9_title",
        paragraphs: [
          "external_api_docs_section_9_body",
        ],
      },
    ],
  },
  en: {
    title: "external_api_docs_title",
    subtitle: "external_api_docs_subtitle",
    sections: [
      {
        title: "external_api_docs_section_1_title",
        paragraphs: [
          "external_api_docs_section_1_body",
        ],
      },
      {
        title: "external_api_docs_section_2_title",
        paragraphs: [
          "external_api_docs_section_2_body",
        ],
      },
      {
        title: "external_api_docs_section_3_title",
        list: [
          "external_api_docs_section_3_item_1",
          "external_api_docs_section_3_item_2",
          "external_api_docs_section_3_item_3",
          "external_api_docs_section_3_item_4",
          "external_api_docs_section_3_item_5",
          "external_api_docs_section_3_item_6",
        ],
      },
      {
        title: "external_api_docs_section_4_title",
        paragraphs: [
          "external_api_docs_section_4_body",
        ],
        list: [
          "external_api_docs_section_4_item_1",
          "external_api_docs_section_4_item_2",
          "external_api_docs_section_4_item_3",
          "external_api_docs_section_4_item_4",
        ],
      },
      {
        title: "external_api_docs_section_5_title",
        paragraphs: [
          "external_api_docs_section_5_body",
        ],
        code: "Authorization: Bearer YOUR_API_KEY",
      },
      {
        title: "external_api_docs_section_6_title",
        code: `fetch("/api/v1/external/contacts", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    "Authorization": "Bearer YOUR_API_KEY"
  },
  body: JSON.stringify({
    full_name: "John Smith",
    email: "john@example.com",
    phone: "+10000000000"
  })
});`,
      },
      {
        title: "external_api_docs_section_7_title",
        paragraphs: [
          "external_api_docs_section_7_body",
        ],
      },
      {
        title: "external_api_docs_section_8_title",
        paragraphs: [
          "external_api_docs_section_8_body",
        ],
      },
      {
        title: "external_api_docs_section_9_title",
        paragraphs: [
          "external_api_docs_section_9_body",
        ],
      },
    ],
  },
};

const SCOPE_GROUPS = [
  {
    key: "contacts",
    titleKey: "external_api_group_contacts",
    descriptionKey: "external_api_group_contacts_description",
    scopes: [
      ["contacts.read", "external_api_scope_read", "Read contacts from CRM"],
      ["contacts.create", "create", "Create new contacts"],
      ["contacts.update", "update", "Update existing contacts"],
    ],
  },
  {
    key: "leads",
    titleKey: "external_api_group_leads",
    descriptionKey: "external_api_group_leads_description",
    scopes: [
      ["leads.read", "external_api_scope_read", "Read CRM leads"],
      ["leads.create", "create", "Create new leads"],
      ["leads.update", "update", "Update existing leads"],
    ],
  },
  {
    key: "tasks",
    titleKey: "external_api_group_tasks",
    descriptionKey: "external_api_group_tasks_description",
    scopes: [
      ["tasks.read", "external_api_scope_read", "Read CRM tasks"],
      ["tasks.create", "create", "Create CRM tasks"],
      ["tasks.update", "update", "Update CRM tasks"],
    ],
  },
  {
    key: "ai",
    titleKey: "external_api_group_ai",
    descriptionKey: "external_api_group_ai_description",
    scopes: [
      ["ai.contact_summary", "external_api_scope_summary", "Generate contact summary"],
      ["ai.contact_next_step", "contact_next_step", "Suggest next CRM action"],
      ["ai.contact_lead_score", "contact_lead_score", "Calculate lead score"],
      ["ai.contact_activity_analysis", "contact_activity_analysis", "Analyze contact activity"],
    ],
  },
];

const ALL_SCOPES = SCOPE_GROUPS.flatMap((group) =>
  group.scopes.map(([scope]) => scope)
);

const PRESETS = [
  {
    key: "readOnly",
    labelKey: "external_api_preset_read_only",
    scopes: ["contacts.read", "leads.read", "tasks.read"],
  },
  {
    key: "crmWrite",
    labelKey: "external_api_preset_crm_write",
    scopes: [
      "contacts.read",
      "contacts.create",
      "contacts.update",
      "leads.read",
      "leads.create",
      "leads.update",
      "tasks.read",
      "tasks.create",
      "tasks.update",
    ],
  },
  {
    key: "aiOnly",
    labelKey: "external_api_preset_ai_only",
    scopes: [
      "contacts.read",
      "ai.contact_summary",
      "ai.contact_next_step",
      "ai.contact_lead_score",
      "ai.contact_activity_analysis",
    ],
  },
  {
    key: "full",
    labelKey: "external_api_preset_full_access",
    scopes: ALL_SCOPES,
  },
];

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return "—";
  }
}

function shortPath(path) {
  if (!path) return "—";
  return path.replace("/api/v1/external", "");
}

function getScopeMeta(scope) {
  for (const group of SCOPE_GROUPS) {
    const found = group.scopes.find(([scopeCode]) => scopeCode === scope);
    if (found) {
      return {
        scope,
        labelKey: found[1],
        description: found[2],
        groupKey: group.key,
        groupTitleKey: group.titleKey,
      };
    }
  }

  return {
    scope,
    labelKey: scope,
    description: scope,
    groupKey: "custom",
    groupTitleKey: "Custom",
  };
}

export default function ExternalApiTab() {
  const { t, i18n } = useTranslation();

  const currentLanguage = i18n.language?.startsWith("ru") ? "ru" : "en";
  const documentation = DOCUMENTATION_CONTENT[currentLanguage];
  const documentationFileUrl = DOCUMENTATION_FILES[currentLanguage];

  const [keys, setKeys] = useState([]);
  const [logs, setLogs] = useState([]);

  const [loadingKeys, setLoadingKeys] = useState(true);
  const [loadingLogs, setLoadingLogs] = useState(true);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [selectedScopes, setSelectedScopes] = useState([
    "contacts.read",
    "contacts.create",
  ]);

  const [keySearch, setKeySearch] = useState("");
  const [logSearch, setLogSearch] = useState("");
  const [createdKey, setCreatedKey] = useState(null);
  const [copied, setCopied] = useState(false);
  const [scopeModalKey, setScopeModalKey] = useState(null);

  const [documentationOpen, setDocumentationOpen] = useState(false);
  const [documentationMinimized, setDocumentationMinimized] = useState(false);

  const selectedSet = useMemo(() => new Set(selectedScopes), [selectedScopes]);

  const totalScopes = useMemo(
    () => SCOPE_GROUPS.reduce((sum, group) => sum + group.scopes.length, 0),
    []
  );

  const filteredKeys = useMemo(() => {
    const q = keySearch.trim().toLowerCase();
    if (!q) return keys;

    return keys.filter((key) => {
      const haystack = [
        key.name,
        key.key_prefix,
        key.is_active ? "active" : "disabled",
        ...(key.scopes || []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(q);
    });
  }, [keys, keySearch]);

  const filteredLogs = useMemo(() => {
    const q = logSearch.trim().toLowerCase();
    if (!q) return logs;

    return logs.filter((log) => {
      const haystack = [
        log.method,
        log.path,
        log.status_code,
        log.ip_address,
        log.created_at,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(q);
    });
  }, [logs, logSearch]);

  const activeKeyForModal = useMemo(() => {
    if (!scopeModalKey) return null;
    return keys.find((key) => key.id === scopeModalKey) || null;
  }, [keys, scopeModalKey]);

  const loadKeys = async () => {
    setLoadingKeys(true);
    try {
      const data = await externalApiKeysApi.list({ limit: 100 });
      setKeys(data.items || []);
    } finally {
      setLoadingKeys(false);
    }
  };

  const loadLogs = async () => {
    setLoadingLogs(true);
    try {
      const data = await externalApiKeysApi.listUsageLogs({ limit: 50 });
      setLogs(data.items || []);
    } finally {
      setLoadingLogs(false);
    }
  };

  useEffect(() => {
    loadKeys();
    loadLogs();
  }, []);

  const setPreset = (preset) => {
    setSelectedScopes(preset.scopes);
  };

  const toggleScope = (scope) => {
    setSelectedScopes((prev) =>
      prev.includes(scope)
        ? prev.filter((item) => item !== scope)
        : [...prev, scope]
    );
  };

  const toggleGroup = (group) => {
    const groupScopes = group.scopes.map(([scope]) => scope);
    const allSelected = groupScopes.every((scope) => selectedSet.has(scope));

    setSelectedScopes((prev) => {
      const current = new Set(prev);

      if (allSelected) {
        groupScopes.forEach((scope) => current.delete(scope));
      } else {
        groupScopes.forEach((scope) => current.add(scope));
      }

      return Array.from(current);
    });
  };

  const selectAll = () => {
    setSelectedScopes(ALL_SCOPES);
  };

  const clearAll = () => {
    setSelectedScopes([]);
  };

  const handleCreate = async () => {
    if (!name.trim()) {
      alert(t("external_api_error_name_required"));
      return;
    }

    if (selectedScopes.length === 0) {
      alert(t("external_api_error_select_scope"));
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        scopes: selectedScopes,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
      };

      const res = await externalApiKeysApi.create(payload);

      setCreatedKey(res.api_key);
      setCopied(false);
      setName("");
      setExpiresAt("");
      setSelectedScopes(["contacts.read", "contacts.create"]);

      await loadKeys();
      await loadLogs();
    } catch (e) {
      alert(e?.response?.data?.detail || t("external_api_error_create_failed"));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (key) => {
    try {
      await externalApiKeysApi.update(key.id, {
        is_active: !key.is_active,
      });
      await loadKeys();
    } catch (e) {
      alert(e?.response?.data?.detail || t("external_api_error_update_failed"));
    }
  };

  const handleDelete = async (key) => {
    if (
      !window.confirm(
        t("external_api_confirm_delete", {
          name: key.name,
        })
      )
    ) {
      return;
    }

    try {
      await externalApiKeysApi.remove(key.id);
      await loadKeys();
      await loadLogs();
    } catch (e) {
      alert(e?.response?.data?.detail || t("external_api_error_delete_failed"));
    }
  };

  const copyCreatedKey = async () => {
    if (!createdKey) return;
    await navigator.clipboard.writeText(createdKey);
    setCopied(true);
  };

  return (
    <div className={styles.externalApiShell}>
      <section className={styles.externalApiHero}>
        <div>
          <div className={styles.externalApiEyebrow}>
            {t("external_api_integrations")}
          </div>
          <h2 className={styles.externalApiTitle}>{t("external_api_title")}</h2>
          <p className={styles.externalApiSubtitle}>
            {t("external_api_subtitle")}
          </p>

          <button
            type="button"
            className={styles.secondaryActionCompact}
            onClick={() => {
              setDocumentationOpen(true);
              setDocumentationMinimized(false);
            }}
          >
            {t("external_api_open_documentation")}
          </button>
        </div>

        <div className={styles.externalApiStats}>
          <div className={styles.externalApiStatCard}>
            <span>{keys.length}</span>
            <small>{t("external_api_total_keys")}</small>
          </div>
          <div className={styles.externalApiStatCard}>
            <span>{keys.filter((key) => key.is_active).length}</span>
            <small>{t("external_api_active_keys")}</small>
          </div>
          <div className={styles.externalApiStatCard}>
            <span>{logs.length}</span>
            <small>{t("external_api_recent_logs")}</small>
          </div>
        </div>
      </section>

      {createdKey && (
        <section className={styles.externalApiSecretBox}>
          <div>
            <strong>{t("external_api_save_key_title")}</strong>
            <p>{t("external_api_save_key_hint")}</p>
            <code>{createdKey}</code>
          </div>

          <button
            type="button"
            className={styles.primaryActionCompact}
            onClick={copyCreatedKey}
          >
            {copied ? t("external_api_copied") : t("external_api_copy_key")}
          </button>
        </section>
      )}

      <section className={styles.externalApiCard}>
        <div className={styles.externalApiCardHead}>
          <div>
            <h3>{t("external_api_create_key")}</h3>
            <p>{t("external_api_create_key_hint")}</p>
          </div>

          <div className={styles.externalApiCreateSummary}>
            <div>
              <strong>{selectedScopes.length}</strong>
              <span>
                {t("external_api_scopes_selected", {
                  selected: selectedScopes.length,
                  total: totalScopes,
                })}
              </span>
            </div>

            <button
              type="button"
              className={styles.primaryActionCompact}
              onClick={handleCreate}
              disabled={saving}
            >
              {saving
                ? t("external_api_creating")
                : t("external_api_create_api_key")}
            </button>
          </div>
        </div>

        <div className={styles.externalApiForm}>
          <label className={styles.externalApiField}>
            <span>{t("external_api_name")}</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("external_api_name_placeholder")}
            />
          </label>

          <label className={styles.externalApiField}>
            <span>{t("external_api_expiration")}</span>
            <input
              type="datetime-local"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
            />
          </label>
        </div>

        <div className={styles.externalApiActionPanel}>
          <div className={styles.externalApiPresetGroup}>
            <span>{t("external_api_presets", { defaultValue: "Presets" })}</span>
            <div>
              {PRESETS.map((preset) => (
                <button
                  key={preset.key}
                  type="button"
                  onClick={() => setPreset(preset)}
                >
                  {t(preset.labelKey)}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.externalApiBulkGroup}>
            <span>
              {t("external_api_bulk_actions", {
                defaultValue: "Bulk actions",
              })}
            </span>
            <div>
              <button type="button" onClick={selectAll}>
                {t("external_api_select_all")}
              </button>
              <button type="button" onClick={clearAll}>
                {t("clear")}
              </button>
            </div>
          </div>
        </div>

        <div className={styles.externalApiScopesGrid}>
          {SCOPE_GROUPS.map((group) => {
            const groupScopes = group.scopes.map(([scope]) => scope);
            const selectedCount = groupScopes.filter((scope) =>
              selectedSet.has(scope)
            ).length;
            const allSelected = selectedCount === groupScopes.length;

            return (
              <div key={group.key} className={styles.externalApiScopeGroup}>
                <div className={styles.externalApiScopeGroupHead}>
                  <div>
                    <h4>{t(group.titleKey)}</h4>
                    <p>{t(group.descriptionKey)}</p>
                  </div>

                  <button type="button" onClick={() => toggleGroup(group)}>
                    {allSelected ? t("clear") : t("external_api_allow_all")}
                  </button>
                </div>

                <div className={styles.externalApiScopeList}>
                  {group.scopes.map(([scope, labelKey, description]) => {
                    const selected = selectedSet.has(scope);

                    return (
                      <button
                        key={scope}
                        type="button"
                        className={`${styles.externalApiScopeItem} ${
                          selected ? styles.externalApiScopeItemActive : ""
                        }`}
                        onClick={() => toggleScope(scope)}
                        title={description}
                      >
                        <span>
                          <strong>{t(labelKey)}</strong>
                          <small>{scope}</small>
                        </span>

                        <i>
                          {selected
                            ? t("external_api_allowed")
                            : t("external_api_off")}
                        </i>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className={styles.externalApiCard}>
        <div className={styles.externalApiCardHead}>
          <div>
            <h3>{t("external_api_existing_keys")}</h3>
            <p>{t("external_api_existing_keys_hint")}</p>
          </div>

          <div className={styles.externalApiInlineTools}>
            <input
              value={keySearch}
              onChange={(e) => setKeySearch(e.target.value)}
              placeholder={t("external_api_search_keys_placeholder")}
            />
            <button
              type="button"
              className={styles.secondaryActionCompact}
              onClick={loadKeys}
            >
              {t("refresh")}
            </button>
          </div>
        </div>

        {loadingKeys ? (
          <div className={styles.externalApiEmpty}>
            {t("external_api_loading_keys")}
          </div>
        ) : filteredKeys.length === 0 ? (
          <div className={styles.externalApiEmpty}>
            {t("external_api_no_matching_keys")}
          </div>
        ) : (
          <div className={styles.externalApiTableWrap}>
            <table className={styles.externalApiKeysTable}>
              <thead>
                <tr>
                  <th>{t("external_api_name")}</th>
                  <th>{t("external_api_prefix")}</th>
                  <th>{t("status")}</th>
                  <th>{t("external_api_scopes")}</th>
                  <th>{t("external_api_last_used")}</th>
                  <th>{t("external_api_expires")}</th>
                  <th>{t("actions")}</th>
                </tr>
              </thead>

              <tbody>
                {filteredKeys.map((key) => {
                  const scopes = key.scopes || [];
                  const visibleScopes = scopes.slice(0, 4);
                  const hiddenCount = Math.max(
                    0,
                    scopes.length - visibleScopes.length
                  );

                  return (
                    <tr key={key.id}>
                      <td>
                        <strong>{key.name}</strong>
                      </td>
                      <td>
                        <code>{key.key_prefix}...</code>
                      </td>
                      <td>
                        <span
                          className={`${styles.externalApiStatus} ${
                            key.is_active ? styles.externalApiStatusActive : ""
                          }`}
                        >
                          {key.is_active
                            ? t("active")
                            : t("external_api_disabled")}
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className={styles.externalApiScopesCell}
                          onClick={() => setScopeModalKey(key.id)}
                          title={t("external_api_view_all_scopes", {
                            defaultValue: "View all permissions",
                          })}
                        >
                          <span className={styles.externalApiTableScopes}>
                            {visibleScopes.map((scope) => (
                              <b key={scope}>{scope}</b>
                            ))}
                            {hiddenCount > 0 && <b>+{hiddenCount}</b>}
                          </span>
                        </button>
                      </td>
                      <td>{formatDate(key.last_used_at)}</td>
                      <td>{formatDate(key.expires_at)}</td>
                      <td>
                        <div className={styles.externalApiTableRowActions}>
                          <button
                            type="button"
                            onClick={() => handleToggleActive(key)}
                          >
                            {key.is_active ? t("disable") : t("enable")}
                          </button>
                          <button
                            type="button"
                            className={styles.externalApiDanger}
                            onClick={() => handleDelete(key)}
                          >
                            {t("delete")}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section
        className={`${styles.externalApiCard} ${styles.externalApiLogsCard}`}
      >
        <div className={styles.externalApiCardHead}>
          <div>
            <h3>{t("external_api_usage_logs")}</h3>
            <p>{t("external_api_usage_logs_hint")}</p>
          </div>

          <div className={styles.externalApiInlineTools}>
            <input
              value={logSearch}
              onChange={(e) => setLogSearch(e.target.value)}
              placeholder={t("external_api_search_logs_placeholder", {
                defaultValue: "Search logs...",
              })}
            />
            <button
              type="button"
              className={styles.secondaryActionCompact}
              onClick={loadLogs}
            >
              {t("external_api_refresh_logs")}
            </button>
          </div>
        </div>

        {loadingLogs ? (
          <div className={styles.externalApiEmpty}>
            {t("external_api_loading_logs")}
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className={styles.externalApiEmpty}>
            {t("external_api_no_usage_logs")}
          </div>
        ) : (
          <div className={styles.externalApiLogs}>
            {filteredLogs.map((log) => (
              <div key={log.id} className={styles.externalApiLogRow}>
                <span>{formatDate(log.created_at)}</span>
                <b>{log.method}</b>
                <code>{shortPath(log.path)}</code>
                <i>{log.status_code ?? "—"}</i>
                <small>{log.ip_address || "—"}</small>
              </div>
            ))}
          </div>
        )}
      </section>

      <section
        className={`${styles.externalApiCard} ${styles.externalApiNoticeCard}`}
      >
        <h3>{t("notifications", { defaultValue: "Notifications" })}</h3>
        <p>
          {t("external_api_notifications_unchanged", {
            defaultValue: "This section is unchanged in this update.",
          })}
        </p>
      </section>

      {documentationOpen && (
        <div className={styles.externalApiModalOverlay}>
          <div
            className={`${styles.externalApiScopesModal} ${
              styles.externalApiDocsModal
            } ${
              documentationMinimized ? styles.externalApiDocsModalMinimized : ""
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.externalApiScopesModalHead}>
              <div>
                <h3>{t(documentation.title)}</h3>

                {!documentationMinimized && <p>{t(documentation.subtitle)}</p>}
              </div>

              <div className={styles.externalApiDocsActions}>
                <button
                  type="button"
                  className={styles.externalApiDocsIconButton}
                  title={
                    documentationMinimized
                      ? t("expand", { defaultValue: "Expand" })
                      : t("collapse", { defaultValue: "Collapse" })
                  }
                  aria-label={
                    documentationMinimized
                      ? t("expand", { defaultValue: "Expand" })
                      : t("collapse", { defaultValue: "Collapse" })
                  }
                  onClick={() => setDocumentationMinimized((prev) => !prev)}
                >
                  {documentationMinimized ? <i class="lni lni-chevron-down"></i> : <i class="lni lni-chevron-up"></i>}
                </button>

                <button
                  type="button"
                  className={styles.externalApiDocsIconButton}
                  title={t("close", { defaultValue: "Close" })}
                  aria-label={t("close", { defaultValue: "Close" })}
                  onClick={() => setDocumentationOpen(false)}
                >
                  <i className="lni lni-xmark"></i>
                </button>
              </div>
            </div>

            {!documentationMinimized && (
              <div className={styles.externalApiDocsContent}>
                {documentation.sections.map((section) => (
                  <React.Fragment key={section.title}>
                    <h4>{t(section.title)}</h4>

                    {section.paragraphs?.map((paragraph) => (
                      <p key={paragraph}>{t(paragraph)}</p>
                    ))}

                    {section.list && (
                      <ul>
                        {section.list.map((item) => (
                          <li key={item}>{t(item)}</li>
                        ))}
                      </ul>
                    )}

                    {section.code && <pre>{section.code}</pre>}
                  </React.Fragment>
                ))}

                <div className={styles.externalApiDocsFileRow}>
                  <a
                    href={documentationFileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.secondaryActionCompact}
                  >
                    {t("external_api_open_docx")}
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {activeKeyForModal && (
        <div
          className={styles.externalApiModalOverlay}
          onClick={() => setScopeModalKey(null)}
        >
          <div
            className={styles.externalApiScopesModal}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.externalApiScopesModalHead}>
              <div>
                <h3>{activeKeyForModal.name}</h3>
                <p>
                  {t("external_api_all_permissions", {
                    defaultValue: "All permissions assigned to this API key.",
                  })}
                </p>
              </div>

              <button type="button" onClick={() => setScopeModalKey(null)}>
                <i className="lni lni-xmark"></i>
              </button>
            </div>

            <div className={styles.externalApiScopesModalList}>
              {(activeKeyForModal.scopes || []).map((scope) => {
                const meta = getScopeMeta(scope);

                return (
                  <div key={scope} className={styles.externalApiScopeModalItem}>
                    <span>
                      <strong>{t(meta.labelKey)}</strong>
                      <small>{scope}</small>
                    </span>
                    <i title={meta.description}>?</i>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
